import { createSlice } from '@reduxjs/toolkit'
import { patientsTexts } from 'locale/es'
import PatientsService from 'services/PatientsService'
import { appendFullNames } from 'utils/HelperFunctions'
import { toast } from './notificationReducer'

const initialState = { data: [], loading: false, error: null }

const allPatientsSlice = createSlice({
  name: 'allPatients',
  initialState,
  reducers: {
    setAllPatients(state, action) {
      const { data } = action.payload

      const parsedPatients = appendFullNames(data)

      return { ...state, data: parsedPatients, loading: false, error: null }
    },
    appendPatient(state, action) {
      const { data } = action.payload

      return { ...state, data: [...state.data, ...appendFullNames([data.patient])], loading: false, error: null }
    },
    removePatient(state, action) {
      const newDataState = state.data.filter((patient) => patient.id !== action.payload)

      return { ...state, data: newDataState, loading: false, error: null }
    },
    updatePatients(state, action) {
      const newState = state.data.map(patient => patient.id !== action.payload.patient.id ? patient : action.payload.patient)

      return { ...state, data: appendFullNames(newState), loading: false, error: null }
    },
    setPatientsLoading(state) {
      state.loading = true;
      state.error = null;
    },
    setPatientsError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    resetPatientsState(state, action) {
      return initialState
    }
  }
})

export const { setAllPatients, appendPatient, removePatient, updatePatients, setPatientsLoading, setPatientsError, resetPatientsState } = allPatientsSlice.actions

export const initializePatients = (centerId) => {
  return async (dispatch) => {
    try {
      dispatch(setPatientsLoading());

      const { data } = await PatientsService.getPatientsByCenter(centerId)
      dispatch(setAllPatients({ data }))
    }
    catch (error) {
      console.error(error)
      dispatch(setPatientsError({ message: error.message, status: error.response?.status }));
      dispatch(toast(patientsTexts.LOADING_ERROR, 'error'))
    }
  }
}

export const createPatientAction = (patientObj) => {
  return async (dispatch) => {
    try {
      const { data } = await PatientsService.createPatient(patientObj)
      dispatch(appendPatient({ data }))
      dispatch(toast(patientsTexts.CREATED, 'success'))
    }
    catch (error) {
      dispatch(setPatientsError({ message: error.message, status: error.response?.status }));
      throw error
    }
  }
}

export const updatePatientAction = (fieldsToUpdate) => {
  return async (dispatch) => {
    try {
      const updatedPatient = await PatientsService.updatePatient(fieldsToUpdate.patientId, fieldsToUpdate)
      dispatch(updatePatients(updatedPatient.data))
      dispatch(toast(patientsTexts.EDITED, 'success'))
    } catch (error) {
      dispatch(setPatientsError({ message: error.message, status: error.response?.status }));
      throw error
    }
  }
}

export const deletePatientAction = (id) => {
  return async (dispatch) => {
    try {
      await PatientsService.deletePatient(id)
      dispatch(removePatient(id))
      dispatch(toast(patientsTexts.DELETED, 'success'))
    } catch (error) {
      console.error(error)
      dispatch(setPatientsError({ message: error.message, status: error.response?.status }));
      dispatch(toast(patientsTexts.DELETED_ERROR, 'error'))
    }
  }
}

export const importPatientsAction = (patients) => {
  return async (dispatch, getState) => {
    try {
      const { data } = await PatientsService.importPatients(patients)

      const returnedPatients = data.createdPatients.length
      const errorCreatingPatients = data.errors.length

      if (errorCreatingPatients && !returnedPatients) {
        throw new Error(data.errors[0])
      }

      const newPatients = data.createdPatients.filter(patient =>
        !getState().allPatients.data.some(newPatient =>
          newPatient.user.email === patient.user.email))

      if (!newPatients.length && errorCreatingPatients) {
        throw new Error(patientsTexts.IMPORT_NOTIFICATIONS.ERRORS.NO_NEW_PATIENTS_AND_ERRORS)
      }

      if (!newPatients.length) {
        throw new Error(patientsTexts.IMPORT_NOTIFICATIONS.ERRORS.NO_NEW_PATIENTS)
      }

      for (const patient of newPatients) {
        dispatch(appendPatient({ data: { patient } }))
      }

      if (newPatients.length && errorCreatingPatients) {
        dispatch(toast(patientsTexts.IMPORT_NOTIFICATIONS.WARNINGS.SOME_ERRORS, "warning", 7))
        return
      }

      if (newPatients.length !== returnedPatients) {
        dispatch(toast(patientsTexts.IMPORT_NOTIFICATIONS.WARNINGS.EXISTING_PATIENTS, "warning"))
        return
      }

      dispatch(toast(patientsTexts.IMPORT_NOTIFICATIONS.SUCCESS, "success"))
    } catch (error) {
      console.error(error)
      dispatch(setPatientsError({ message: error.message, status: error.response?.status }));
      dispatch(toast(error.message, 'error', 10))
    }
  }
}

export default allPatientsSlice.reducer